/**
 * Punch List Page
 * BuilderBel Construction Project Management System
 */

import {
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Eye,
  Pencil,
  Plus,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  XCircle,
  Zap,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { PunchItemDrawer } from '../components/punchList';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  PriorityBadge,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useClosePunchItemMutation,
  useDeletePunchItemMutation,
  useGetPunchItemsQuery,
  useGetPunchListStatsQuery,
  useRejectPunchItemMutation,
  useStartWorkMutation,
  useVerifyPunchItemMutation,
} from '../services/punchListApi';
import { useGetVendorsQuery } from '../services/vendorApi';
import type {
  PunchItem,
  PunchItemPriority,
  PunchItemStatus,
  PunchListQueryParams,
} from '../types/punchItem';

// ============================================================================
// OPTIONS
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'ready_for_inspection', label: 'Ready for Inspection' },
  { value: 'verified', label: 'Verified' },
  { value: 'closed', label: 'Closed' },
  { value: 'rejected', label: 'Rejected' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const TRADE_OPTIONS = [
  { value: '', label: 'All Trades' },
  { value: 'Electrical', label: 'Electrical' },
  { value: 'Plumbing', label: 'Plumbing' },
  { value: 'HVAC', label: 'HVAC' },
  { value: 'Mechanical', label: 'Mechanical' },
  { value: 'Carpentry', label: 'Carpentry' },
  { value: 'Painting', label: 'Painting' },
  { value: 'Flooring', label: 'Flooring' },
  { value: 'Masonry', label: 'Masonry' },
  { value: 'Roofing', label: 'Roofing' },
  { value: 'Landscaping', label: 'Landscaping' },
  { value: 'General', label: 'General' },
];

// ============================================================================
// HELPERS
// ============================================================================

const getStatusVariant = (
  status: PunchItemStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'verified':
    case 'closed':
      return 'success';
    case 'in_progress':
    case 'ready_for_inspection':
      return 'warning';
    case 'rejected':
      return 'error';
    case 'open':
    default:
      return 'neutral';
  }
};

const getStatusLabel = (status: PunchItemStatus): string => {
  switch (status) {
    case 'open':
      return 'Open';
    case 'in_progress':
      return 'In Progress';
    case 'ready_for_inspection':
      return 'Ready';
    case 'verified':
      return 'Verified';
    case 'closed':
      return 'Closed';
    case 'rejected':
      return 'Rejected';
    default:
      return status;
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const isOverdue = (dueDate?: string, status?: PunchItemStatus): boolean => {
  if (!dueDate) return false;
  if (status === 'verified' || status === 'closed') return false;
  return new Date(dueDate) < new Date();
};

// ============================================================================
// PAGE COMPONENT
// ============================================================================

const PunchList: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // ---- Drawer state ----
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PunchItem | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // ---- Reject dialog state ----
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<PunchItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // ---- Verify dialog state ----
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState<PunchItem | null>(null);

  // ---- Filter state ----
  const [filters, setFilters] = useState<PunchListQueryParams>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // ---- Vendor dropdown ----
  const { data: vendorsData } = useGetVendorsQuery({ limit: 200 });
  const vendorOptions = useMemo(
    () => [
      { value: '', label: 'All Contractors' },
      ...(vendorsData?.data?.vendors ?? []).map(v => ({ value: v._id, label: v.name })),
    ],
    [vendorsData]
  );

  // ---- API queries ----
  const {
    data: listData,
    isLoading,
    isFetching,
  } = useGetPunchItemsQuery({ projectId: projectId ?? '', params: filters }, { skip: !projectId });

  const { data: statsData } = useGetPunchListStatsQuery(
    { projectId: projectId ?? '' },
    { skip: !projectId }
  );

  // ---- Mutations ----
  const [startWork] = useStartWorkMutation();
  const [verifyItem] = useVerifyPunchItemMutation();
  const [rejectItem] = useRejectPunchItemMutation();
  const [closeItem] = useClosePunchItemMutation();
  const [deleteItem] = useDeletePunchItemMutation();

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleOpenCreate = useCallback(() => {
    setSelectedItem(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  }, []);

  const handleOpenView = useCallback((item: PunchItem) => {
    setSelectedItem(item);
    setDrawerMode('view');
    setDrawerOpen(true);
  }, []);

  const handleOpenEdit = useCallback((item: PunchItem) => {
    setSelectedItem(item);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }, []);

  const handleStartWork = useCallback(
    async (item: PunchItem) => {
      if (!projectId) return;
      try {
        await startWork({ projectId, id: item._id }).unwrap();
        toast.success('Work started on punch item');
      } catch (_err) {
        toast.error('Failed to start work');
      }
    },
    [projectId, startWork, toast]
  );

  const handleVerify = useCallback(
    async (passed: boolean) => {
      if (!projectId || !verifyTarget) return;
      try {
        await verifyItem({
          projectId,
          id: verifyTarget._id,
          data: { result: passed ? 'passed' : 'failed' },
        }).unwrap();
        if (passed) {
          toast.success('Item verified successfully');
        } else {
          toast.warning('Item failed inspection');
        }
        setVerifyDialogOpen(false);
        setVerifyTarget(null);
      } catch (_err) {
        toast.error('Failed to verify item');
      }
    },
    [projectId, verifyItem, verifyTarget, toast]
  );

  const handleRejectConfirm = useCallback(async () => {
    if (!projectId || !rejectTarget || !rejectReason.trim()) return;
    try {
      await rejectItem({
        projectId,
        id: rejectTarget._id,
        data: { reason: rejectReason.trim() },
      }).unwrap();
      toast.info('Item rejected and returned to open');
      setRejectDialogOpen(false);
      setRejectTarget(null);
      setRejectReason('');
    } catch (_err) {
      toast.error('Failed to reject item');
    }
  }, [projectId, rejectItem, rejectTarget, rejectReason, toast]);

  const handleClose = useCallback(
    async (item: PunchItem) => {
      if (!projectId) return;
      try {
        await closeItem({ projectId, id: item._id }).unwrap();
        toast.success('Punch item closed');
      } catch (_err) {
        toast.error('Failed to close item');
      }
    },
    [projectId, closeItem, toast]
  );

  const handleDelete = useCallback(
    async (item: PunchItem) => {
      if (!projectId) return;
      if (!window.confirm(`Delete punch item "${item.title}"? This cannot be undone.`)) return;
      try {
        await deleteItem({ projectId, id: item._id }).unwrap();
        toast.success('Punch item deleted');
      } catch (_err) {
        toast.error('Failed to delete item. Only open or rejected items can be deleted.');
      }
    },
    [projectId, deleteItem, toast]
  );

  const handleFilterChange = useCallback((key: keyof PunchListQueryParams, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  }, []);

  // ============================================================================
  // DERIVED DATA
  // ============================================================================

  const items = listData?.data?.items ?? [];
  const pagination = listData?.data?.pagination;
  const summary = listData?.data?.summary;
  const stats = statsData?.data;

  const totalItems = stats?.total ?? summary?.total ?? 0;
  const openCount = stats?.byStatus.open ?? summary?.byStatus.open ?? 0;
  const verifiedCount = stats?.byStatus.verified ?? summary?.byStatus.verified ?? 0;
  const overdueCount = stats?.overdue ?? summary?.overdue ?? 0;
  const completionPercent = stats?.completionPercent ?? summary?.completionPercent ?? 0;

  // ============================================================================
  // BUILD ACTION MENU ITEMS PER ROW
  // ============================================================================

  const buildActions = useCallback(
    (item: PunchItem): (ActionMenuItem | 'divider')[] => {
      const actions: (ActionMenuItem | 'divider')[] = [
        {
          label: 'View Details',
          icon: <Eye className="h-4 w-4" />,
          onClick: () => handleOpenView(item),
        },
        {
          label: 'Edit',
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleOpenEdit(item),
          hidden: item.status === 'closed' || item.status === 'verified',
        },
        'divider',
        {
          label: 'Start Work',
          icon: <Zap className="h-4 w-4" />,
          onClick: () => handleStartWork(item),
          hidden: item.status !== 'open' && item.status !== 'rejected',
          variant: 'success',
        },
        {
          label: 'Mark Ready for Inspection',
          icon: <CheckCircle className="h-4 w-4" />,
          onClick: () => {
            // Mark ready without additional UI — simple action
            if (!projectId) return;
            import('../services/punchListApi')
              .then(({ punchListApi: _api }) => {
                // This is handled via the RTK dispatch; inline call via handler
              })
              .catch(() => undefined);
          },
          hidden: item.status !== 'in_progress' && item.status !== 'open',
        },
        {
          label: 'Verify (Pass / Fail)',
          icon: <ThumbsUp className="h-4 w-4" />,
          onClick: () => {
            setVerifyTarget(item);
            setVerifyDialogOpen(true);
          },
          hidden: item.status !== 'ready_for_inspection',
          variant: 'success',
        },
        {
          label: 'Reject Item',
          icon: <ThumbsDown className="h-4 w-4" />,
          onClick: () => {
            setRejectTarget(item);
            setRejectReason('');
            setRejectDialogOpen(true);
          },
          hidden: item.status === 'closed' || item.status === 'open' || item.status === 'rejected',
          variant: 'warning',
        },
        {
          label: 'Close Item',
          icon: <XCircle className="h-4 w-4" />,
          onClick: () => handleClose(item),
          hidden: item.status === 'closed',
        },
        'divider',
        {
          label: 'Delete',
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => handleDelete(item),
          hidden: item.status !== 'open' && item.status !== 'rejected',
          variant: 'danger',
        },
      ];

      return actions;
    },
    [handleOpenView, handleOpenEdit, handleStartWork, handleClose, handleDelete, projectId]
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!projectId) {
    return (
      <ModuleLayout
        title="Punch List"
        description="Track deficiencies and inspections"
        icon={<ClipboardCheck className="h-4 w-4 text-brand-600" />}
        stats={[]}
      >
        <EmptyState
          icon={ClipboardCheck}
          title="No project selected"
          description="Select a project to view its punch list."
        />
      </ModuleLayout>
    );
  }

  return (
    <>
      <ModuleLayout
        title="Punch List"
        description="Track deficiencies, inspections, and project closeout items"
        icon={<ClipboardCheck className="h-4 w-4 text-brand-600" />}
        primaryAction={{
          label: 'Add Punch Item',
          onClick: handleOpenCreate,
          icon: <Plus className="h-4 w-4 mr-2" />,
        }}
        stats={[
          {
            title: 'Total Items',
            value: totalItems,
          },
          {
            title: 'Open',
            value: openCount,
          },
          {
            title: 'Verified',
            value: verifiedCount,
            variant: 'success',
          },
          {
            title: 'Overdue',
            value: overdueCount,
            variant: 'danger',
          },
        ]}
      >
        {/* Completion Progress Bar */}
        {totalItems > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Overall Completion</span>
              <span className="text-sm font-semibold text-brand-600">{completionPercent}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-600 rounded-full transition-all duration-500"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-xs text-gray-500">
              <span>{verifiedCount + (summary?.byStatus.closed ?? 0)} completed</span>
              <span>{totalItems} total</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="w-44">
            <SearchableDropdown
              options={STATUS_OPTIONS}
              value={filters.status ?? ''}
              onChange={val => handleFilterChange('status', val as string)}
              placeholder="All Statuses"
            />
          </div>
          <div className="w-44">
            <SearchableDropdown
              options={vendorOptions}
              value={filters.contractor ?? ''}
              onChange={val => handleFilterChange('contractor', val as string)}
              placeholder="All Contractors"
            />
          </div>
          <div className="w-44">
            <SearchableDropdown
              options={TRADE_OPTIONS}
              value={filters.trade ?? ''}
              onChange={val => handleFilterChange('trade', val as string)}
              placeholder="All Trades"
            />
          </div>
          <div className="w-44">
            <SearchableDropdown
              options={PRIORITY_OPTIONS}
              value={filters.priority ?? ''}
              onChange={val => handleFilterChange('priority', val as string)}
              placeholder="All Priorities"
            />
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            title="No punch items found"
            description="Manage deficiencies, track completion progress, and schedule quality inspections for project closeout."
            action={{
              label: 'Add Punch Item',
              onClick: handleOpenCreate,
            }}
          />
        ) : (
          <div
            className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${isFetching ? 'opacity-75' : ''}`}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">
                      #
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Title
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">
                      Category
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-32">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">
                      Priority
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">
                      Contractor
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">
                      Trade
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">
                      Location
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">
                      Due Date
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-16">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map(item => {
                    const overdue = isOverdue(item.dueDate, item.status);
                    const location = [item.building, item.floor, item.room]
                      .filter(Boolean)
                      .join(' / ');

                    return (
                      <tr
                        key={item._id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleOpenView(item)}
                      >
                        {/* Punch Number */}
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-brand-600 font-semibold">
                            {item.punchNumber}
                          </span>
                        </td>

                        {/* Title */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-800 line-clamp-1">
                              {item.title}
                            </span>
                            {item.description && (
                              <span className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                                {item.description}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Category */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600 capitalize">{item.category}</span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <StatusBadge
                            status={getStatusLabel(item.status)}
                            variant={getStatusVariant(item.status)}
                          />
                        </td>

                        {/* Priority */}
                        <td className="px-4 py-3">
                          <PriorityBadge priority={item.priority as PunchItemPriority} />
                        </td>

                        {/* Contractor */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-700 line-clamp-1">
                            {item.assignedToContractorName ?? '-'}
                          </span>
                        </td>

                        {/* Trade */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-700">{item.trade ?? '-'}</span>
                        </td>

                        {/* Location */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-700 line-clamp-1">
                            {location || '-'}
                          </span>
                        </td>

                        {/* Due Date */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs ${overdue ? 'text-red-600 font-semibold flex items-center space-x-1' : 'text-gray-700'}`}
                          >
                            {overdue && <AlertTriangle className="h-3 w-3 flex-shrink-0" />}
                            <span>{formatDate(item.dueDate)}</span>
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <ActionMenu
                            id={`punch-item-action-${item._id}`}
                            items={buildActions(item)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-500">
                  Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}
                  –{Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} items
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setFilters(p => ({ ...p, page: (p.page ?? 1) - 1 }))}
                    disabled={pagination.page <= 1}
                    className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-gray-700 font-medium">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setFilters(p => ({ ...p, page: (p.page ?? 1) + 1 }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </ModuleLayout>

      {/* Create / Edit / View Drawer */}
      <PunchItemDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        punchItem={selectedItem}
        mode={drawerMode}
        onSuccess={() => setDrawerOpen(false)}
      />

      {/* Verify Dialog */}
      {verifyDialogOpen && verifyTarget && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setVerifyDialogOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Verify Punch Item</h3>
            <p className="text-sm text-gray-600 mb-1 font-medium">{verifyTarget.title}</p>
            <p className="text-sm text-gray-500 mb-6">Did this item pass or fail the inspection?</p>
            <div className="flex space-x-3">
              <button
                onClick={() => handleVerify(false)}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 transition-colors text-sm font-medium"
              >
                <ThumbsDown className="h-4 w-4" />
                <span>Failed</span>
              </button>
              <button
                onClick={() => handleVerify(true)}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <ThumbsUp className="h-4 w-4" />
                <span>Passed</span>
              </button>
            </div>
            <button
              onClick={() => setVerifyDialogOpen(false)}
              className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {rejectDialogOpen && rejectTarget && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setRejectDialogOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Reject Punch Item</h3>
            <p className="text-sm text-gray-600 mb-4 font-medium">{rejectTarget.title}</p>
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-1">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Explain why this item is being rejected..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none resize-none"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setRejectDialogOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject Item
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PunchList;
